import _ from 'lodash';
import moment from 'moment';
import { MessageContext } from 'fluent/compat';

import formeater from 'assets/images/badges/formeater.png';
import lilslugger from 'assets/images/badges/lilslugger.png';
import nightowl from 'assets/images/badges/nightowl.png';
import streaker from 'assets/images/badges/streaker.png';
import sunnysideup from 'assets/images/badges/sunnysideup.png';
import weekdaywarrior from 'assets/images/badges/weekdaywarrior.png';
import weekendwarrior from 'assets/images/badges/weekendwarrior.png';
import youreonfire from 'assets/images/badges/youreonfire.png';

const badgePics = {
  total_forms_completed: formeater,
  sunny_side_up: sunnysideup,
  night_owl: nightowl,
  longest_streak: streaker,
  first_exercise: lilslugger,
  weekday_warrior: weekdaywarrior,
  weekend_warrior: weekendwarrior,
  total_tasks_completed: youreonfire,
};

const badgeContent = new MessageContext('en-US');
badgeContent.addMessages(`
title = {$badge ->
  [total_forms_completed] Form Eater
  [sunny_side_up] Sunny Side Up
  [night_owl] Night Owl
  [first_exercise] Lil' Slugger
  [weekend_warrior] Weekend Warrior
  [weekday_warrior] Weekday Warrior
  [total_tasks_completed] You're on Fire
  [longest_streak] Streaker
}

description = {$badge ->
  [total_forms_completed]
    You fed the form eater!

  [sunny_side_up]
    You're an early bird!

  [night_owl]
    You're a night owl!

  [first_exercise]
    Oh hey there, lil' slugger.

  [weekend_warrior]
    Here's to the weekend!

  [weekday_warrior]
    Rollin' your sleeves up!

  [total_tasks_completed]
    You're on fire!

  [longest_streak]
    You've gone streaking!

 *[default]
    We don't have a description for this badge yet!
}

purpose = {$badge ->
  [total_forms_completed] {$brand ->
    [shi]
      Way to get all your forms completed!
      Question sets are an important way to track your progress
      and they also help us map your journey.
    *[other]
      You feed him each time you complete a form.
      You'll unlock more badges when you've fed
      him 3, 5, and 10 forms.
  }

  [sunny_side_up] {$brand ->
    [shi]
      Eggs over easy!
      Getting into a routine can be a challenge.
      We applaud trying different times of day to see what works best for you.

    *[other]
      You've completed 25 tasks before 9:00am.
      Way to go! The early bird gets the worm.
      In this case, you get a badge.
  }

  [night_owl] {$brand ->
     [shi]
      Hoot Hoot!
      Getting into a routine can be a challenge.
      We applaud trying different times of day to see what works best for you.
    *[other]
      You've completed 25 tasks after 9:00pm.
      Way to go! Here's a badge for checking
      in during the late hours.
  }

  [first_exercise] {$brand ->
     [shi]
      What could be more important than taking the first step in the right direction!
    *[other]
      Hooray! You completed your first exercise.
      Keep coming back to see what other badges
      are in store for you.
  }

  [weekend_warrior] {$brand ->
     [shi]
      Weekends are a great time to add to or solidify a new routine.
    *[other]
      You've completed 25 tasks on weekends.
      That makes you a Weekend Warrior.
      Are you a Weekday Warrior too?
  }

  [weekday_warrior] {$brand ->
     [shi]
      Lots of people wait until the weekend for “chores”.
      Health is a 24/7 deal.
      Trying to fit in tasks during the week will help keep you ahead of the curve.
    *[other]
      You've completed 25 tasks on weekdays.
      That makes you a Weekday Warrior.
      Are you a Weekend Warrior too?
  }

  [total_tasks_completed] {$brand ->
     [shi]
      Keep up the hustle!
    *[other]
      You completed your first 5 tasks, and we're feelin' proud of you.
      |
      You're probably going to be assigned many tasks on your way
      to better movement, and we think you should be rewarded for them.
      Each time you reach a new level, you'll earn another badge like
      this one. Your next one comes when you reach 10 tasks completed,
      then 25, 100, and 200.
  }

  [longest_streak] {$brand ->
     [shi]
      You’ve successfully completed all of your tasks for 5 days in a row.
      Consistency makes us durable.
    *[other]
      You completed one task every day for the past 3 days.
      Can you keep the streak going? You'll earn another badge
      each week (or 7 days). Keep on movin' on.
  }

 *[default]
}
`);

export const getBadgeDetails = (badges, badgeKey) => {
  const badgeObj = _.find(badges, { name: badgeKey });
  return {
    ...badgeObj,
    id: badgeKey,
    image: badgePics[badgeKey],
    title: badgeContent.format(badgeContent.getMessage('title'), { badge: badgeKey }),
    description: badgeContent.format(badgeContent.getMessage('description'), { badge: badgeKey }),
    purpose: badgeContent.format(badgeContent.getMessage('purpose'), { badge: badgeKey }),
    earned: !!badgeObj,
  };
};

export const getUpdatedBadges = badges =>
  _.keys(badgePics).map(badgeKey =>
    getBadgeDetails(badges, badgeKey));

export const STRATUM = [
  {
    key: 'disease_index',
    label: 'Disease',
  },
  {
    key: 'stress_index',
    label: 'Stress',
  },
  {
    key: 'movement_quality',
    label: 'Movement',
  },
  {
    key: 'fitness_index',
    label: 'Fitness',
  },
  {
    key: 'nutrition_index',
    label: 'Nutrition',
  },
];

export const getIndexesOverTime = (measureReports = [], identifier) => {
  const values = measureReports.map((report) => {
    const { endedAt, groups } = report;
    return _.map(groups, (group) => {
      const { value, measureScore } = group.stratifier[0].stratum[0];
      return {
        value,
        measureScore,
        endedAt,
      };
    });
  });

  const totalHealthScores = _.filter(_.flatten(values), ['value', identifier]);
  return _.sortBy(totalHealthScores, ['endedAt']);
};

export const getHealthIndex = (measureReports = [], identifier) => {
  const indexesOverTime = getIndexesOverTime(measureReports, identifier);

  if (indexesOverTime.length > 0) {
    const latestScore = _.last(indexesOverTime);
    return latestScore.measureScore;
  }
  return 0;
};

export function getTotalScoreOverTime(measureReports = [], identifier, isNull, formatValuesToDisplay) {
  const indexesOverTime = _.takeRight(getIndexesOverTime(measureReports, identifier), 7);
  const formatScore = score => (formatValuesToDisplay ? formatValuesToDisplay(score) : score);
  return _.map(indexesOverTime, score => ({
    identifier,
    xLabel: moment.utc(score.endedAt).format('MM-DD-YYYY'),
    value: isNull ? null : formatScore(score.measureScore),
  }));
}

export const datePlaceholder = _.reverse(_.times(7, index => ({
  xLabel: moment().utc().subtract(index, 'week').format('YYYY-MM-DD'),
  value: 0,
})));
